/*
 * Description:
 * ---------------------
 * Encryption panel — per-traffic-class tunnel payload encryption.
 *
 * THE SHAPE OF THE SCREEN
 *   Two decisions, in order, and nothing else is a control:
 *     1. How strong should the encryption be?
 *     2. Which traffic should get it?
 *   Everything above them is the answer to "am I protected right now"; every-
 *   thing below them is a readout. The per-class list and the per-class
 *   strength override are both behind disclosures, because neither is needed
 *   to reach a correct configuration.
 *
 * WHAT "OFF" MEANS, AND HOW IT IS WORDED
 *   This is an ADDITIONAL layer, not the only one. A class left off still
 *   carries whatever the applications themselves negotiate — HTTPS, TLS, a
 *   guest's own VPN. So off is a legitimate default, never a warning: no amber,
 *   no "unsealed", no "in the clear". The screen says what NABx is adding, and
 *   leaves the app layer its credit.
 *
 * WHAT THE APPLIANCE ACTUALLY DOES
 *   A class left off stays on the in-kernel eBPF fast path: no copy, no
 *   syscall, full bonding and failover. A class switched on is diverted to a
 *   userspace lane that seals every packet before it leaves. That costs a copy
 *   per packet and means a single sustained stream rides one link instead of
 *   being striped — stated on screen next to the choice, not buried.
 *
 * THE POST-QUANTUM TIER
 *   Changes the KEY EXCHANGE, not the cipher. AES-256 is already quantum-
 *   resistant; what a quantum computer breaks is the X25519 handshake, by
 *   recording traffic today and decrypting it later. Per-packet cost is
 *   identical to Strong.
 *
 * ! file EncryptionPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiShield, FiShieldOff, FiLock, FiCpu, FiZap, FiAward, FiCheck,
  FiChevronDown, FiAlertTriangle, FiInfo, FiActivity, FiSliders,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useToast } from "./ToastProvider";
import { useConfirm } from "./ConfirmModal";

/* ── Plain-language tiers ────────────────────────────────────────────────────
   The picker used to be a dropdown of cipher-suite ids. Those name the
   algorithm, which is the one thing an operator choosing protection does not
   need to decide. The id still goes to the appliance and is still shown in the
   details, so a reviewer can confirm the primitive — it is just not the thing
   you must understand in order to choose. */
const TIER = {
  off:       { label: "Off",           icon: FiShieldOff, color: "#94A3B8",
               blurb: "No extra layer. Keeps its own protection, on the fast path." },
  standard:  { label: "Standard",      icon: FiZap,       color: "#0EA5E9",
               blurb: "Encrypted, at the lowest CPU cost.",
               cost: "Lightest option. Good for busy appliances." },
  strong:    { label: "Strong",        icon: FiShield,    color: "#10B981",
               blurb: "AES-256. The right choice for almost everything.",
               cost: "Same speed as Standard on any modern CPU." },
  pq:        { label: "Quantum-safe",  icon: FiLock,      color: "#8B5CF6",
               blurb: "Also survives a future quantum computer.",
               cost: "Identical per-packet cost to Strong." },
  mobile:    { label: "Low-power",     icon: FiCpu,       color: "#F59E0B",
               blurb: "For hardware with no AES acceleration.",
               cost: "~3× slower than Strong on AES-capable hardware." },
  "pq-max":  { label: "Quantum-safe+", icon: FiAward,     color: "#A855F7",
               blurb: "Highest post-quantum parameters, for compliance.",
               cost: "Same per-packet cost; a larger handshake." },
};

const tierOf = (id) =>
  TIER[id] || { label: id || "—", icon: FiShield, color: "#94A3B8", blurb: "" };

/* The three tiers that answer every ordinary question. The other two are
   answers to specific constraints — no AES in hardware, or a CNSA 2.0
   requirement — so they live behind "more options" rather than competing for
   attention with the one most people want. */
const PRIMARY_TIERS = ["standard", "strong", "pq"];

/* Traffic worth encrypting when you do not want to encrypt everything:
   conversations and control. Computed from the class's own category rather
   than a hand-kept list, so a new app class lands in the right bucket. */
const SENSITIVE = new Set(["CRITICAL", "REALTIME", "INTERACTIVE"]);
const isSensitive = (c) => SENSITIVE.has(c.isApp ? c.category : c.key);

/* A tier the edge cannot negotiate — normally its identity key was provisioned
   below the ML-DSA level the tier needs. An older edge omits the field, so
   absence must read as "usable", not "blocked". */
const blockedTier = (s) => s.available === false;

const fmtBytes = (b) => {
  if (b == null || !isFinite(b)) return "—";
  if (b >= 1e12) return `${(b / 1e12).toFixed(2)} TB`;
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(1)} KB`;
  return `${b} B`;
};
const fmtCount = (n) => (n == null || !isFinite(n) ? "—" : Number(n).toLocaleString());

export default function EncryptionPanel({ nodeApiUrl, selectedNode, trafficClasses = [] }) {
  const [suites, setSuites] = useState([]);
  const [status, setStatus] = useState(null);
  const [perClass, setPerClass] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  /* The operator's chosen strength. Null until they pick one or the appliance
     reveals what is already armed — see `strength` below. */
  const [picked, setPicked] = useState(null);
  const [showMoreTiers, setShowMoreTiers] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showApps, setShowApps] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [perClassTier, setPerClassTier] = useState(false);

  const toast = useToast();
  const confirm = useConfirm();

  const api = useCallback(async (path, opts, timeout = 10000) => {
    const res = await fetchWithTimeout(nodeApiUrl(path), opts, timeout);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text.trim() || `${res.status} ${res.statusText}`);
    }
    return res.json();
  }, [nodeApiUrl]);

  const refresh = useCallback(async () => {
    try {
      const [suiteRes, statusRes, policyRes] = await Promise.all([
        api("/encryption/suites"),
        api("/encryption/status"),
        api("/policy/encryption"),
      ]);
      setSuites(suiteRes.suites || []);
      setStatus(statusRes || {});
      setPerClass(policyRes.encryption || {});
      setError("");
    } catch (err) {
      setError(err.message || "Could not read the encryption settings from this edge.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh, selectedNode]);

  /* ── What is armed, counted over the classes this panel actually lists ─────
     The edge knows ~70 classes; TRAFFIC_CLASSES lists 34. Counting raw policy
     keys would report "70 of 34 encrypted", so both halves of the ratio come
     from the same set. */
  const shownKeys = useMemo(
    () => new Set(trafficClasses.map((c) => c.key)), [trafficClasses]);

  const armedKeys = useMemo(
    () => Object.entries(perClass)
      .filter(([k, s]) => shownKeys.has(k) && s && s !== "off")
      .map(([k]) => k),
    [perClass, shownKeys]);

  const total = trafficClasses.length;
  const armedCount = armedKeys.length;
  const allArmed = total > 0 && armedCount >= total;

  const sensitiveKeys = useMemo(
    () => trafficClasses.filter(isSensitive).map((c) => c.key), [trafficClasses]);

  /* Exact match, not "some but not all" — a hand-picked selection of three
     classes is not the sensitive preset, and labelling it Current would be a
     claim about which classes are armed. */
  const onSensitivePreset = useMemo(
    () => armedCount === sensitiveKeys.length
      && sensitiveKeys.every((k) => armedKeys.includes(k)),
    [armedCount, armedKeys, sensitiveKeys]);

  /* The strength in force. Whatever most armed classes use, so the picker
     reflects the appliance rather than the last thing clicked in this tab. */
  const dominant = useMemo(() => {
    const tally = {};
    armedKeys.forEach((k) => { tally[perClass[k]] = (tally[perClass[k]] || 0) + 1; });
    const best = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    return best ? best[0] : null;
  }, [armedKeys, perClass]);

  const recommended = useMemo(
    () => suites.find((s) => s.recommended && s.id !== "off")?.id || "strong",
    [suites]);

  const strength = picked || dominant || recommended;

  /* Armed classes not using the chosen strength. Worth naming: the picker
     shows one tier while the appliance is running several. */
  const oddOnes = useMemo(
    () => armedKeys.filter((k) => perClass[k] !== strength),
    [armedKeys, perClass, strength]);

  /* ── Preconditions ────────────────────────────────────────────────────────
     The edge REFUSES to arm a class while the userspace lane is stopped: a
     non-zero enc_mode diverts the class off the fast path to a TUN nobody is
     reading, which blackholes it silently. Disabling is always permitted. */
  const available = status?.available !== false;
  const blocked = status?.blocked_reason;
  const laneReady = status?.lane_running === true;
  const canArm = available && !blocked && laneReady;

  const lane = status?.lane || {};
  const sessionsByAnchor = status?.sessions_by_anchor;
  const liveSessions = sessionsByAnchor
    ? Object.values(sessionsByAnchor).reduce((n, bySuite) => n + Object.keys(bySuite || {}).length, 0)
    : Object.keys(status?.sessions || {}).length;
  const anchorCount = sessionsByAnchor ? Object.keys(sessionsByAnchor).length : 0;

  const dropped = (lane.tx_drop_no_key || 0) + (lane.tx_drop_no_route || 0) + (lane.tx_drop_error || 0);

  /* ── Writes ───────────────────────────────────────────────────────────────
     One helper per shape of change. The appliance's refusals name a
     precondition the operator can act on, so they are surfaced verbatim. */

  const runningRef = useRef(false);
  const guard = async (label, fn) => {
    if (runningRef.current) return;
    runningRef.current = true;
    setBusy(label);
    setError("");
    try {
      await fn();
      await refresh();
    } catch (err) {
      const msg = err.message || "The edge refused that change.";
      setError(msg);
      toast.error(msg);
    } finally {
      runningRef.current = false;
      setBusy("");
      setProgress(null);
    }
  };

  const putClass = (classKey, suite) =>
    api("/policy/encryption", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ class: classKey, suite }),
    });

  const putAll = (suite) =>
    api("/policy/encryption/all", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ suite }),
    }, 20000);

  /* Everything: one request under one lock. There is no server-side category
     inheritance, so "everything" really is every class the edge knows, and
     doing it as 34 browser PUTs could land half-applied. */
  const applyEverything = () => guard("Encrypting everything", async () => {
    const res = await putAll(strength);
    const n = res?.classes;
    toast.success(n
      ? `All ${n} traffic classes now carry the extra layer.`
      : "All traffic classes now carry the extra layer.");
  });

  const applyNothing = () => guard("Turning encryption off", async () => {
    await putAll("off");
    toast.success("Extra encryption off. Everything is back on the fast path, keeping its own protection.");
  });

  /* The middle option, and the one most sites want. Clear first so the result
     is exactly the preset rather than the preset unioned with whatever was
     already armed. */
  const applySensitive = () => guard("Encrypting sensitive traffic", async () => {
    await putAll("off");
    setProgress({ done: 0, total: sensitiveKeys.length });
    for (let i = 0; i < sensitiveKeys.length; i += 4) {
      const batch = sensitiveKeys.slice(i, i + 4);
      await Promise.all(batch.map((k) => putClass(k, strength)));
      setProgress({ done: Math.min(i + 4, sensitiveKeys.length), total: sensitiveKeys.length });
    }
    toast.success(`${sensitiveKeys.length} sensitive traffic classes now carry the extra layer.`);
  });

  const toggleClass = (cls, on) => guard(`Updating ${cls.label}`, async () => {
    await putClass(cls.key, on ? strength : "off");
  });

  const setClassTier = (cls, suite) => guard(`Updating ${cls.label}`, async () => {
    await putClass(cls.key, suite);
  });

  /* Changing the strength restyles what is already armed — that is what the
     control appears to promise. With nothing armed it just records the choice
     for the next thing turned on. */
  const chooseStrength = (id) => {
    setPicked(id);
    if (!armedCount) return;
    guard("Changing strength", async () => {
      if (allArmed) {
        await putAll(id);
      } else {
        setProgress({ done: 0, total: armedKeys.length });
        for (let i = 0; i < armedKeys.length; i += 4) {
          const batch = armedKeys.slice(i, i + 4);
          await Promise.all(batch.map((k) => putClass(k, id)));
          setProgress({ done: Math.min(i + 4, armedKeys.length), total: armedKeys.length });
        }
      }
      toast.success(`Encrypted traffic now uses ${tierOf(id).label}.`);
    });
  };

  const confirmEverything = async () => {
    const ok = await confirm({
      title: "Encrypt every traffic class?",
      message:
        "Every class moves off the in-kernel fast path into the userspace lane, so the whole appliance pays a copy per packet.\n\n" +
        "Multiple connections still bond across WANs and still fail over, but a single sustained stream can no longer be striped across links.\n\n" +
        "On a quiet link this is fine. Under load, encrypt sensitive traffic only.",
      confirmLabel: "Encrypt everything",
      variant: "primary",
    });
    if (ok) applyEverything();
  };

  const categories = useMemo(() => trafficClasses.filter((c) => !c.isApp), [trafficClasses]);
  const apps = useMemo(() => trafficClasses.filter((c) => c.isApp), [trafficClasses]);

  const tierList = useMemo(() => {
    const known = suites.filter((s) => s.id !== "off");
    const primary = PRIMARY_TIERS.map((id) => known.find((s) => s.id === id)).filter(Boolean);
    const rest = known.filter((s) => !PRIMARY_TIERS.includes(s.id));
    return { primary, rest };
  }, [suites]);

  if (loading) {
    return (
      <div className="tp-page">
        <div className="tp-card tp-loading">Reading the encryption settings…</div>
      </div>
    );
  }

  const working = !!busy;
  const pct = total ? Math.round((armedCount / total) * 100) : 0;

  return (
    <div className="tp-page">

      {/* ── Blocking preconditions, before any control they disable ───────── */}
      {blocked && (
        <Alert kind="danger" title="Encryption is unavailable">{blocked}</Alert>
      )}

      {!available && !blocked && (
        <Alert kind="warn" title="This edge has no encryption control">
          It may be running an older build, or running in direct mode.
        </Alert>
      )}

      {available && !blocked && !laneReady && (
        <Alert kind="warn" title="The encrypted lane is stopped — nothing can be turned on">
          Switching a class on diverts it off the in-kernel fast path to the userspace
          lane. With no lane running, those packets would be dropped silently, so the
          edge refuses. Turning classes <em>off</em> still works.
          {status?.lane_detail ? <div className="tp-alert-detail">{status.lane_detail}</div> : null}
        </Alert>
      )}

      {error && <Alert kind="danger" title="That change did not apply">{error}</Alert>}

      {/* ── 1. The answer ─────────────────────────────────────────────────── */}
      <section className={`tp-card tp-verdict ${armedCount ? "is-on" : "is-off"}`}>
        <div className="tp-verdict-main">
          <div className="tp-verdict-icon">
            {armedCount ? <FiShield size={22} /> : <FiShieldOff size={22} />}
          </div>
          <div className="tp-verdict-text">
            <h3>
              {armedCount === 0
                ? "No extra encryption layer"
                : allArmed
                  ? "Every kind of traffic gets an extra layer"
                  : `${armedCount} of ${total} kinds of traffic get an extra layer`}
            </h3>
            <p>
              {armedCount === 0
                ? <>Traffic keeps the protection it already carries (HTTPS, TLS, a
                     guest's own VPN) and rides the in-kernel fast path at full
                     speed. Turn this on to seal it a second time inside the
                     tunnel.</>
                : <>Wrapped a second time with <strong>{tierOf(strength).label}</strong>
                    {suites.find((s) => s.id === strength)?.cipher
                      ? ` (${suites.find((s) => s.id === strength).cipher})` : ""}
                    , on top of whatever protection the traffic already carries.
                    {allArmed ? "" : " The rest keeps just its own, on the fast path."}</>}
            </p>
          </div>
        </div>

        <div className="tp-meter" role="img"
             aria-label={`${armedCount} of ${total} traffic classes encrypted`}>
          <div className="tp-meter-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="tp-meter-key">
          <span>{pct}% of traffic classes</span>
          <span className={`tp-dot ${laneReady ? "ok" : "off"}`} />
          <span>
            {laneReady ? "Lane running" : "Lane stopped"}
            {liveSessions ? ` · ${liveSessions} live key${liveSessions === 1 ? "" : "s"}` : ""}
            {anchorCount > 1 ? ` across ${anchorCount} anchors` : ""}
          </span>
        </div>
      </section>

      {/* ── 2. Step one: strength ─────────────────────────────────────────── */}
      <section className="tp-card">
        <StepHead n={1} title="Choose how strong"
                  sub="This is the cipher every encrypted class uses. Changing it re-applies to everything already on." />

        <div className="tp-choices">
          {tierList.primary.map((s) => (
            <TierCard key={s.id} suite={s} on={strength === s.id}
                      disabled={working || !canArm} onPick={() => chooseStrength(s.id)} />
          ))}
        </div>

        {tierList.rest.length > 0 && (
          <>
            <button type="button" className="tp-disclose"
                    onClick={() => setShowMoreTiers((v) => !v)}>
              <FiChevronDown size={14} className={showMoreTiers ? "rot" : ""} />
              More options ({tierList.rest.length}) — for specific hardware or compliance rules
            </button>
            {showMoreTiers && (
              <div className="tp-choices tp-choices--sub">
                {tierList.rest.map((s) => (
                  <TierCard key={s.id} suite={s} on={strength === s.id}
                            disabled={working || !canArm} onPick={() => chooseStrength(s.id)} />
                ))}
              </div>
            )}
          </>
        )}

        <TierDetail suite={suites.find((s) => s.id === strength)} />

        {oddOnes.length > 0 && (
          <div className="tp-note tp-note--warn">
            <FiInfo size={13} />
            <span>
              {oddOnes.length} encrypted class{oddOnes.length === 1 ? "" : "es"} still
              use a different strength.
            </span>
            <button className="tp-btn tp-btn--sm" disabled={working}
                    onClick={() => chooseStrength(strength)}>
              Put them all on {tierOf(strength).label}
            </button>
          </div>
        )}
      </section>

      {/* ── 3. Step two: coverage ─────────────────────────────────────────── */}
      <section className="tp-card">
        <StepHead n={2} title="Choose what to encrypt"
                  sub="Encrypted traffic leaves the in-kernel fast path. Pick a preset, or open the list and choose class by class." />

        <div className="tp-presets">
          <Preset
            title="Sensitive traffic only"
            sub="Calls, messaging and control get the extra layer. Video and downloads stay on the fast path."
            tag="Recommended"
            on={onSensitivePreset}
            disabled={working || !canArm || onSensitivePreset}
            onClick={applySensitive}
          />
          <Preset
            title="Everything"
            sub="Every class gets the extra layer. Strongest, and the whole appliance pays for it."
            on={allArmed}
            disabled={working || !canArm || allArmed}
            onClick={confirmEverything}
          />
          <Preset
            title="Nothing"
            sub="No NABx layer anywhere. Apps keep their own protection. Full in-kernel bonding, zero added cost."
            on={armedCount === 0}
            disabled={working || !available || armedCount === 0}
            onClick={applyNothing}
          />
        </div>

        {progress && (
          <div className="tp-progress">
            <div className="tp-progress-fill"
                 style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
            <span>{busy} — {progress.done} of {progress.total}</span>
          </div>
        )}

        {armedCount > 0 && (
          <div className="tp-note">
            <FiActivity size={13} />
            <span>
              <strong>What encryption costs.</strong> Encrypted classes pay a userspace
              copy per packet. They still bond — many connections still aggregate across
              WANs and still fail over — but one sustained stream now rides a single link,
              and its bytes stop showing in the per-WAN counters on other panels.
            </span>
          </div>
        )}

        <button type="button" className="tp-disclose" onClick={() => setShowList((v) => !v)}>
          <FiChevronDown size={14} className={showList ? "rot" : ""} />
          Choose class by class ({total})
        </button>

        {showList && (
          <div className="tp-list-wrap">
            <div className="tp-list-tools">
              <label className="tp-check">
                <input type="checkbox" checked={perClassTier}
                       onChange={(e) => setPerClassTier(e.target.checked)} />
                <FiSliders size={12} /> Let each class use a different strength
              </label>
            </div>

            <div className="tp-list-head">Traffic categories</div>
            <div className="tp-list">
              {categories.map((c) => (
                <ClassRow key={c.key} cls={c} suite={perClass[c.key] || "off"}
                          strength={strength} suites={suites} allowTier={perClassTier}
                          disabled={working || !available}
                          canArm={canArm} counters={(status?.per_class || {})[c.key]}
                          onToggle={toggleClass} onTier={setClassTier} />
              ))}
            </div>

            <button type="button" className="tp-disclose tp-disclose--nested"
                    onClick={() => setShowApps((v) => !v)}>
              <FiChevronDown size={14} className={showApps ? "rot" : ""} />
              Individual applications ({apps.length})
            </button>
            {showApps && (
              <div className="tp-list">
                {apps.map((c) => (
                  <ClassRow key={c.key} cls={c} suite={perClass[c.key] || "off"}
                            strength={strength} suites={suites} allowTier={perClassTier}
                            disabled={working || !available}
                            canArm={canArm} counters={(status?.per_class || {})[c.key]}
                            onToggle={toggleClass} onTier={setClassTier} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── 4. Readout ────────────────────────────────────────────────────── */}
      <section className="tp-card">
        <button type="button" className="tp-disclose" onClick={() => setShowActivity((v) => !v)}>
          <FiChevronDown size={14} className={showActivity ? "rot" : ""} />
          What the encrypted lane is doing
          {(dropped > 0 || lane.rx_drop_auth > 0) && (
            <span className="tp-disclose-flag">needs a look</span>
          )}
        </button>

        {showActivity && (
          <div className="tp-stats">
            <Stat label="Encrypted and sent" value={fmtBytes(lane.tx_bytes)}
                  sub={`${fmtCount(lane.tx_packets)} packets`} />
            <Stat label="Live keys" value={fmtCount(liveSessions)}
                  sub={anchorCount > 1 ? `across ${anchorCount} anchors` : "with the anchor"} />
            <Stat label="Dropped rather than sent unwrapped" value={fmtCount(dropped)}
                  sub="fail-closed: an encrypted class is never quietly downgraded"
                  warn={dropped > 0} />
            <Stat label="Forged or replayed packets rejected" value={fmtCount(lane.rx_drop_auth)}
                  sub="someone else's packets, refused"
                  warn={lane.rx_drop_auth > 0} />
          </div>
        )}
      </section>
    </div>
  );
}

/* ── Pieces ──────────────────────────────────────────────────────────────── */

function StepHead({ n, title, sub }) {
  return (
    <div className="tp-step">
      <span className="tp-step-n">{n}</span>
      <div>
        <b>{title}</b>
        <span>{sub}</span>
      </div>
    </div>
  );
}

function Alert({ kind, title, children }) {
  return (
    <div className={`tp-alert tp-alert--${kind}`}>
      <FiAlertTriangle size={16} />
      <div>
        <b>{title}</b>
        <div>{children}</div>
      </div>
    </div>
  );
}

function TierCard({ suite, on, disabled, onPick }) {
  const meta = tierOf(suite.id);
  const Icon = meta.icon;
  const unusable = blockedTier(suite);
  return (
    <button
      type="button"
      className={`tp-choice${on ? " is-on" : ""}`}
      style={{ "--c": meta.color }}
      disabled={disabled || unusable}
      title={unusable ? suite.unavailable_reason : undefined}
      onClick={onPick}
    >
      <span className="tp-choice-top">
        <span className="tp-choice-ico"><Icon size={16} /></span>
        <span className="tp-choice-label">{meta.label}</span>
        {suite.recommended && !on && <span className="tp-choice-tag">Recommended</span>}
        {on && <FiCheck size={15} className="tp-choice-check" />}
      </span>
      <span className="tp-choice-blurb">{meta.blurb}</span>
      {unusable
        ? <span className="tp-choice-blocked">Unavailable on this edge</span>
        : meta.cost && <span className="tp-choice-cost">{meta.cost}</span>}
    </button>
  );
}

/* The primitives, for whoever needs to confirm them. Deliberately small and
   below the choice: nobody has to read this to pick correctly. */
function TierDetail({ suite }) {
  if (!suite) return null;
  return (
    <div className="tp-detail">
      <span><b>Cipher</b> {suite.cipher}</span>
      <span><b>Key exchange</b> {suite.key_exchange}</span>
      {suite.signature && <span><b>Signature</b> {suite.signature}</span>}
      {suite.post_quantum && <span className="tp-detail-tag">post-quantum</span>}
      {blockedTier(suite) && suite.unavailable_reason && (
        <span className="tp-detail-warn">{suite.unavailable_reason}</span>
      )}
    </div>
  );
}

function Preset({ title, sub, tag, on, disabled, onClick }) {
  return (
    <button type="button" className={`tp-preset${on ? " is-on" : ""}`}
            disabled={disabled} onClick={onClick}>
      <span className="tp-preset-top">
        <span className="tp-preset-title">{title}</span>
        {tag && !on && <span className="tp-choice-tag">{tag}</span>}
        {on && <span className="tp-preset-now"><FiCheck size={13} /> Current</span>}
      </span>
      <span className="tp-preset-sub">{sub}</span>
    </button>
  );
}

function ClassRow({ cls, suite, strength, suites, allowTier, disabled, canArm, counters, onToggle, onTier }) {
  const on = suite !== "off";
  const meta = tierOf(on ? suite : strength);
  return (
    <div className={`tp-row${on ? " is-on" : ""}`}>
      <div className="tp-row-main">
        <span className="tp-row-name">{cls.label}</span>
        {cls.hint && <span className="tp-row-hint">{cls.hint}</span>}
      </div>

      {/* Only while the class is on: "Off · 145 B sealed" is a counter from an
          earlier arming, and reads as a contradiction. */}
      {on && counters?.tx_packets > 0 && (
        <span className="tp-row-count">{fmtBytes(counters.tx_bytes)} sealed</span>
      )}

      {allowTier && on ? (
        <select className="tp-row-select" value={suite} disabled={disabled}
                onChange={(e) => onTier(cls, e.target.value)}>
          {suites.filter((s) => s.id !== "off" && !blockedTier(s)).map((s) => (
            <option key={s.id} value={s.id}>{tierOf(s.id).label}</option>
          ))}
        </select>
      ) : (
        <span className={`tp-row-tier${on ? "" : " is-off"}`}
              style={{ "--c": meta.color }}>
          {on ? meta.label : "Off"}
        </span>
      )}

      <button type="button" className={`tp-switch${on ? " is-on" : ""}`}
              role="switch" aria-checked={on} aria-label={`Encrypt ${cls.label}`}
              disabled={disabled || (!on && !canArm)}
              title={!on && !canArm ? "The encrypted lane is not running" : undefined}
              onClick={() => onToggle(cls, !on)}>
        <span className="tp-switch-knob" />
      </button>
    </div>
  );
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className="tp-stat">
      <div className="tp-stat-label">{label}</div>
      <div className={`tp-stat-value${warn ? " warn" : ""}`}>{value}</div>
      {sub && <div className="tp-stat-sub">{sub}</div>}
    </div>
  );
}
